// hooks/usePerformance.js
import { useEffect, useRef, useState, useCallback } from 'react';
import PerformanceMonitor from '../utils/performanceMonitor';

/**
 * Hook for monitoring component performance
 */
export function usePerformance(componentName) {
  const mountTimeRef = useRef(null);
  const renderCountRef = useRef(0);
  const [performanceData, setPerformanceData] = useState(null);

  // Mark component mount start
  useEffect(() => {
    mountTimeRef.current = performance.now();
    PerformanceMonitor.markComponentMount(componentName);
    
    return () => {
      // Measure total component lifetime
      if (mountTimeRef.current) {
        const lifetime = performance.now() - mountTimeRef.current;
        PerformanceMonitor.recordMetric('componentLifetime', lifetime, { 
          component: componentName,
          renderCount: renderCountRef.current
        });
      }
    };
  }, [componentName]);

  // Measure component mount completion
  useEffect(() => {
    PerformanceMonitor.measureComponentMount(componentName);
  });

  // Track render count
  useEffect(() => {
    renderCountRef.current++;
    PerformanceMonitor.recordMetric('componentRender', renderCountRef.current, { 
      component: componentName 
    });
  });

  // Get performance data
  const getPerformanceData = useCallback(() => {
    const summary = PerformanceMonitor.getSummary();
    const componentMetrics = {
      renderCount: renderCountRef.current,
      mountTime: mountTimeRef.current,
      lifetime: mountTimeRef.current ? performance.now() - mountTimeRef.current : 0
    };
    
    return {
      ...summary,
      component: componentMetrics
    };
  }, []);

  return {
    performanceData,
    getPerformanceData,
    renderCount: renderCountRef.current
  };
}

/**
 * Hook for measuring operation performance
 */
export function useOperationPerformance() {
  const [operations, setOperations] = useState([]);

  const measureOperation = useCallback(async (name, operation) => {
    const startTime = performance.now();
    let result, error;
    
    try {
      result = await operation();
    } catch (err) {
      error = err;
    }
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    const operationData = {
      name,
      duration,
      timestamp: Date.now(),
      success: !error,
      error: error?.message
    };
    
    setOperations(prev => [...prev.slice(-19), operationData]); // Keep last 20
    PerformanceMonitor.recordMetric(`operation_${name}`, duration, operationData);
    
    if (error) throw error;
    return result;
  }, []);

  return {
    operations,
    measureOperation
  };
}

/**
 * Hook for monitoring render performance
 */
export function useRenderPerformance(componentName, dependencies = []) {
  const renderStartRef = useRef(null);
  const renderCountRef = useRef(0);
  const [renderMetrics, setRenderMetrics] = useState({
    count: 0,
    averageDuration: 0,
    lastDuration: 0
  });

  // Start render timing
  renderStartRef.current = performance.now();
  renderCountRef.current++;

  useEffect(() => {
    // End render timing
    if (renderStartRef.current) {
      const renderDuration = performance.now() - renderStartRef.current;
      
      setRenderMetrics(prev => {
        const newCount = renderCountRef.current;
        const newAverage = ((prev.averageDuration * (newCount - 1)) + renderDuration) / newCount;
        
        return {
          count: newCount,
          averageDuration: newAverage,
          lastDuration: renderDuration
        };
      });

      PerformanceMonitor.recordMetric('renderDuration', renderDuration, {
        component: componentName,
        renderCount: renderCountRef.current
      });

      // Warn about slow renders
      if (renderDuration > 16) { // 60fps threshold
        console.warn(`Slow render in ${componentName}: ${renderDuration.toFixed(2)}ms`);
      }
    }
  }, dependencies);

  return renderMetrics;
}

/**
 * Hook for monitoring memory usage
 */
export function useMemoryMonitoring(interval = 5000) {
  const [memoryInfo, setMemoryInfo] = useState(null);

  useEffect(() => {
    if (!('memory' in performance)) return;

    const updateMemoryInfo = () => {
      const memory = performance.memory;
      const info = {
        used: memory.usedJSHeapSize,
        total: memory.totalJSHeapSize,
        limit: memory.jsHeapSizeLimit,
        percentage: (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100,
        timestamp: Date.now()
      };
      
      setMemoryInfo(info);
      PerformanceMonitor.recordMetric('memorySnapshot', info);
    };

    updateMemoryInfo();
    const intervalId = setInterval(updateMemoryInfo, interval);

    return () => clearInterval(intervalId);
  }, [interval]);

  return memoryInfo;
}

/**
 * Hook for performance recommendations
 */
export function usePerformanceRecommendations() {
  const [recommendations, setRecommendations] = useState([]);

  useEffect(() => {
    const updateRecommendations = () => {
      const summary = PerformanceMonitor.getSummary();
      setRecommendations(summary.recommendations || []);
    };

    updateRecommendations();
    
    // Update recommendations every 30 seconds
    const intervalId = setInterval(updateRecommendations, 30000);

    return () => clearInterval(intervalId);
  }, []);

  return recommendations;
}

export default usePerformance;